import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useSubscription } from '../context/SubscriptionContext';
import { getColors, fonts } from '../theme';
import { Section } from '../components/Notebook';
import { useAgent } from './AgentContext';
import { useSuggestions } from './suggestions';
import { assistantName } from './assistantName';
import { useVoice } from './voice';
import { withoutDashes, withoutEmoji } from '../utils/text';

// The assistant, as a chatbox on Today. Messages are rows in the notebook
// margin layout; things it did show as mono lines, like entries in a log.
// The conversation lives in AgentProvider, so it survives leaving the page.

const SUGGESTIONS = [
  'How am I doing this week?',
  'Mark everything I did this morning',
  'Plan tomorrow around my open tasks',
];

const TOP_FADE = 'linear-gradient(to bottom, transparent 0, #000 28px)';

// The text box grows with what's in it, a line (24px) at a time, from one
// line to four, then scrolls inside
const LINE_PX = 24;
const BOX_MIN = LINE_PX + 16; // one line plus its padding
const BOX_MAX = 4 * LINE_PX + 16;
// The composer's footprint at one line (box, padding, border, margins). On the
// home chat it holds exactly this much room and grows upward over the
// conversation, so nothing around it moves
const COMPOSER_SLOT = BOX_MIN + 8 + 2 + 8 + 12;

const formatTime = (date) => date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

// Rows use the notebook margin from md up; on phones the margin would take a
// fifth of the width from every reply, so text runs full width and the time
// and marks sit inline instead.
const ROW = 'grid grid-cols-[minmax(0,1fr)] md:grid-cols-[44px_minmax(0,1fr)] gap-x-3';
const MARGIN = 'hidden md:block font-mono text-[11px] pt-[3px] tabular-nums';

// A thing the assistant did, in the log's plain mono. A folded run ("DARE:
// done on 54 days") opens to list its days
const ActionEntry = ({ entry, colors }) => {
  const [open, setOpen] = useState(false);
  const mark = entry.muted ? '·' : '×';
  const dates = entry.group?.dates || [];
  const color = entry.muted ? colors.textMuted : colors.textSecondary;
  const text = (
    <>
      <span className="md:hidden mr-2" aria-hidden="true">{mark}</span>
      {withoutEmoji(entry.text)}
    </>
  );
  return (
    <div className={`${ROW} py-0.5 agent-fade`}>
      <span className={`${MARGIN} pt-0`} style={{ color: colors.textMuted }} aria-hidden="true">{mark}</span>
      {dates.length > 1 ? (
        <div>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            className="font-mono text-[12px] text-left break-words hover:underline underline-offset-4"
            style={{ color }}
          >
            {text}
          </button>
          {open && (
            <p className="font-mono text-[11px] leading-relaxed pt-1" style={{ color: colors.textMuted }}>
              {[...dates].sort().join('  ')}
            </p>
          )}
        </div>
      ) : (
        <p className="font-mono text-[12px] break-words" style={{ color }}>{text}</p>
      )}
    </div>
  );
};

const Entry = ({ entry, colors }) => {
  if (entry.kind === 'user') {
    return (
      <div className={`${ROW} pt-5 pb-2`} style={{ borderTop: `1px solid ${colors.border}` }}>
        <span className={MARGIN} style={{ color: colors.textMuted }}>{formatTime(entry.time)}</span>
        <p className="text-[15px] font-medium whitespace-pre-wrap break-words" style={{ color: colors.textPrimary }}>
          <span className="md:hidden font-mono text-[11px] font-normal tabular-nums mr-2" style={{ color: colors.textMuted }}>{formatTime(entry.time)}</span>
          {entry.text}
        </p>
      </div>
    );
  }
  if (entry.kind === 'action') return <ActionEntry entry={entry} colors={colors} />;
  if (entry.kind === 'error') {
    return (
      <div className={`${ROW} py-2 agent-fade`}>
        <span className="hidden md:block" />
        <p className="text-[14px]" style={{ color: colors.error }}>{entry.text}</p>
      </div>
    );
  }
  return (
    <div className={`${ROW} py-2 agent-fade`}>
      <span className="hidden md:block" />
      <p className="text-[15px] leading-relaxed whitespace-pre-wrap break-words" style={{ color: colors.textPrimary }}>{withoutDashes(entry.text)}</p>
    </div>
  );
};

const Confirm = ({ request, onAnswer, colors }) => (
  <div className={`${ROW} py-3 agent-fade`}>
    <span className={MARGIN} style={{ color: colors.textMuted }} aria-hidden="true">?</span>
    <div className="p-3" style={{ border: `1px solid ${colors.textPrimary}` }} role="alertdialog" aria-label="Confirm">
      <p className="text-[15px] mb-3" style={{ color: colors.textPrimary }}>{request.prompt}</p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onAnswer(true)}
          className="px-3 min-h-[40px] text-sm font-medium"
          style={{
            backgroundColor: request.safe ? colors.accent : colors.error,
            color: colors.onAccent,
          }}
          autoFocus
        >
          {request.action}
        </button>
        <button
          type="button"
          onClick={() => onAnswer(false)}
          className="px-3 min-h-[40px] text-sm"
          style={{ border: `1px solid ${colors.borderStrong}`, color: colors.textPrimary }}
        >
          {request.safe ? 'Cancel' : 'Keep'}
        </button>
      </div>
    </div>
  </div>
);

// Conversation height before it scrolls inside its own cap (inline layout)
const LOG_MAX_HEIGHT = 360;

// Two layouts: inline, a Section on the full Today page whose log caps at
// LOG_MAX_HEIGHT; and `fill`, the home screen's chat (the one-screen Today):
// it takes the whole height it's given, scrolls the log inside and pins the
// composer to the bottom, with suggestions to tap above it until there's a
// conversation. `brief` (inline only) is a line about the day above it;
// `greeting` (fill only) greets an empty conversation.
const AgentChat = ({ fill = false, brief = null, greeting = null }) => {
  const { isDark, agentProfile } = useApp();
  const { isPro, loading: tierLoading } = useSubscription();
  // What the user calls it, if they've named it (Settings > Assistant memory)
  const name = assistantName(agentProfile);
  const colors = getColors(isDark);
  const {
    entries, busy, send, reset, pendingConfirm, answerConfirm, canUndo, undoTurn,
    nextPrompt, archive, continueArchive,
  } = useAgent();
  // Reading yesterday's conversation (the archive) instead of today's
  const [viewingArchive, setViewingArchive] = useState(false);

  // Talking instead of typing: tap the mic, speak, and it's sent when you
  // stop. Replies stay as text
  const voice = useVoice();
  const voiceOn = voice.listening;
  const [draft, setDraft] = useState('');
  const scrollRef = useRef(null);
  // Once scrolled, the log's top edge fades, so a line passing under the top
  // of the panel dissolves instead of being cut in half
  const [scrolled, setScrolled] = useState(false);
  const onScroll = (e) => setScrolled(e.currentTarget.scrollTop > 0);

  // Follow the conversation to its latest line
  useEffect(() => {
    const el = scrollRef.current;
    if (el && entries.length > 0) el.scrollTop = el.scrollHeight;
  }, [entries, pendingConfirm, busy]);

  const needsUpgrade = (!tierLoading && !isPro) || entries.some((e) => e.code === 'upgrade');
  const empty = entries.length === 0;
  const suggested = useSuggestions(fill && !tierLoading && !needsUpgrade && empty);

  const submit = (text = draft) => {
    if (!text.trim() || busy) return;
    // Writing while reading yesterday's conversation carries it on
    if (viewingArchive) {
      continueArchive();
      setViewingArchive(false);
    }
    send(text);
    setDraft('');
  };

  // Stopping keeps what was heard in the box, to edit or send
  const stopVoice = () => voice.stopListening();
  const startVoice = () => {
    setDraft('');
    voice.listen({
      onText: setDraft,
      // Sent when the user stops talking; nothing heard, nothing sent
      onDone: (heard) => {
        if (heard) submit(heard);
        else setDraft('');
      },
    });
  };

  const mutedButton = 'font-mono text-xs hover:underline underline-offset-4';

  const aside = entries.length > 0 && !busy ? (
    <button type="button" onClick={reset} className={mutedButton} style={{ color: colors.textSecondary }}>
      New conversation
    </button>
  ) : null;

  const briefLine = brief && (
    <p className="text-[15px] italic leading-snug pt-2.5 pb-1" style={{ fontFamily: fonts.serif, color: colors.textPrimary }}>
      {brief}
    </p>
  );

  // What the user might ask next, as they'd say it; tapping sends it. Quiet
  // lines under the greeting, part of the conversation's opening rather
  // than buttons on top of it
  const chips = suggested.length > 0 && (
    <div className="pt-3 pb-1 agent-fade" aria-label="Suggestions">
      {suggested.map((s, i) => (
        <button
          key={`${i}-${s.label}`}
          type="button"
          onClick={() => submit(s.prompt)}
          disabled={busy}
          title={s.prompt}
          className="flex items-baseline gap-2 w-full min-h-[36px] text-left text-[15px] hover:underline underline-offset-4 disabled:opacity-40"
          style={{ color: colors.textSecondary }}
        >
          <span aria-hidden="true" className="font-mono text-[12px]" style={{ color: colors.textMuted }}>→</span>
          <span>{s.label}</span>
        </button>
      ))}
    </div>
  );

  const upgrade = (
    <div className="py-3 flex flex-wrap items-center justify-between gap-3">
      <p className="text-[15px]" style={{ color: colors.textSecondary }}>
        The assistant can add, check off and plan anything in mind. for you. It's part of Pro.
      </p>
      <Link
        to="/upgrade"
        className="px-4 min-h-[44px] inline-flex items-center text-sm font-medium"
        style={{ backgroundColor: colors.accent, color: colors.onAccent }}
      >
        See Pro
      </Link>
    </div>
  );

  const log = (
    <>
      {entries.map((entry) => <Entry key={entry.id} entry={entry} colors={colors} />)}
      {pendingConfirm && <Confirm request={pendingConfirm} onAnswer={answerConfirm} colors={colors} />}
      {canUndo && (
        <div className="md:pl-[56px] pt-1">
          <button
            type="button"
            onClick={undoTurn}
            className="font-mono text-[12px] underline underline-offset-4 min-h-[32px]"
            style={{ color: colors.textSecondary }}
          >
            Undo these changes
          </button>
        </div>
      )}
    </>
  );

  const suggestions = (vertical) => (
    <div className={vertical ? 'pt-2' : 'flex flex-wrap gap-x-5 gap-y-1 pt-2'}>
      {SUGGESTIONS.map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => submit(s)}
          className={`${mutedButton} min-h-[32px] text-left ${vertical ? 'block' : ''}`}
          style={{ color: colors.textSecondary }}
        >
          {s}
        </button>
      ))}
    </div>
  );

  // One box: the text and, once there's something to send, an ink arrow
  // inside it. No separate Send block or underline to stack against the tab
  // bar's rule
  // After a reply, what it suggests asking next waits in the empty text box,
  // faint, with a small › beside it, the way Claude offers a reply: tap the ›
  // (or press Tab or →) to put it in the box, then edit or send it. The
  // suggestion comes with the reply itself (its "Next:" line), so offering it
  // costs no extra request
  const suggestion = viewingArchive || busy || voiceOn ? null : nextPrompt;
  const showSuggestion = !!suggestion && !draft;
  const canSend = !busy && !!draft.trim();
  const inputRef = useRef(null);

  // The box's height follows its text: an invisible copy with the same width
  // and type is measured (setting the box itself to auto would jump, not
  // glide), and the box eases to that height
  const measureRef = useRef(null);
  const [boxHeight, setBoxHeight] = useState(BOX_MIN);
  useLayoutEffect(() => {
    const measure = () => {
      const el = measureRef.current;
      if (el) setBoxHeight(Math.min(BOX_MAX, Math.max(BOX_MIN, el.scrollHeight)));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [draft]);
  // While listening, keep the newest words in view
  useEffect(() => {
    const el = inputRef.current;
    if (el && voice.listening) el.scrollTop = el.scrollHeight;
  }, [draft, voice.listening, boxHeight]);
  // Words being spoken sit centred, like a caption; typed text stays left
  const captioning = voice.listening;
  const takeSuggestion = () => {
    setDraft(suggestion);
    // Into the box with the caret at the end, ready to edit or send
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(suggestion.length, suggestion.length);
    });
  };
  const composer = (
    <form
      className="flex items-end gap-1 mt-2 mb-3 pl-3 pr-1 py-1"
      style={{ border: `1px solid ${colors.borderStrong}` }}
      onSubmit={(e) => { e.preventDefault(); submit(); }}
    >
      <div className="relative flex-1 min-w-0">
        <textarea
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              submit();
            } else if (showSuggestion && (e.key === 'Tab' || e.key === 'ArrowRight')) {
              e.preventDefault();
              takeSuggestion();
            }
          }}
          rows={1}
          maxLength={4000}
          placeholder={showSuggestion ? '' : voice.listening ? 'Listening…'
            : busy ? 'Working…' : voice.error || (name ? `Ask ${name} anything` : 'Ask anything, or say what to do')}
          aria-label={`Message ${name || 'the assistant'}`}
          aria-describedby={showSuggestion ? 'next-suggestion' : undefined}
          className="block w-full resize-none bg-transparent outline-none text-[16px] leading-6 py-2"
          style={{
            color: colors.textPrimary,
            height: boxHeight,
            overflowY: boxHeight >= BOX_MAX ? 'auto' : 'hidden',
            textAlign: captioning ? 'center' : 'left',
            transition: 'height 180ms cubic-bezier(0.2, 0.7, 0.2, 1)',
          }}
        />
        {/* The measuring copy: same width, type and wrapping as the box */}
        <div
          ref={measureRef}
          aria-hidden="true"
          className="absolute left-0 right-0 top-0 invisible pointer-events-none whitespace-pre-wrap break-words text-[16px] leading-6 py-2"
        >
          {`${draft}\u200b`}
        </div>
        {showSuggestion && (
          // Over the empty box: the text lets taps through to the box, the › takes it
          <div className="absolute inset-0 flex items-center gap-2 pointer-events-none">
            <span id="next-suggestion" className="min-w-0 truncate text-[16px]" style={{ color: colors.textMuted }}>
              {suggestion}
            </span>
            <button
              type="button"
              onClick={takeSuggestion}
              aria-label={`Use suggestion: ${suggestion}`}
              title="Use this (Tab)"
              className="pointer-events-auto flex-none w-8 h-8 grid place-items-center font-mono text-[15px] agent-fade"
              style={{ border: `1px solid ${colors.borderStrong}`, color: colors.textSecondary }}
            >
              ›
            </button>
          </div>
        )}
      </div>
      {/* One place on the right: send when there's text; otherwise the mic
          (where the browser can listen), or stop while talking */}
      {voiceOn ? (
        <button
          type="button"
          onClick={stopVoice}
          aria-label="Stop listening"
          title="Stop"
          className="w-10 h-10 flex-none grid place-items-center text-[13px]"
          style={{ backgroundColor: colors.accent, color: colors.onAccent }}
        >
          ■
        </button>
      ) : !draft.trim() && voice.supported && !viewingArchive ? (
        <button
          type="button"
          onClick={startVoice}
          disabled={busy}
          aria-label={`Talk to ${name || 'the assistant'}`}
          title="Talk"
          className="w-10 h-10 flex-none grid place-items-center disabled:opacity-40"
          style={{ border: `1px solid ${colors.borderStrong}`, color: colors.textPrimary }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <rect x="5.5" y="1.5" width="5" height="8.5" rx="2.5" />
            <path d="M3 7.5a5 5 0 0 0 10 0M8 12.5V15" strokeLinecap="round" />
          </svg>
        </button>
      ) : (
        <button
          type="submit"
          disabled={!canSend}
          aria-label="Send"
          title="Send"
          className="w-10 h-10 flex-none grid place-items-center text-[18px] transition-opacity duration-150"
          style={{ backgroundColor: colors.accent, color: colors.onAccent, opacity: canSend ? 1 : 0 }}
        >
          ↑
        </button>
      )}
    </form>
  );

  // Yesterday's conversation, in either layout: a quiet link on an empty
  // chat, and a reader with a way back and a way to carry it on
  const reading = viewingArchive && archive;
  const archiveDay = archive && new Date(`${archive.day}T00:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  const archiveLink = archive && (
    <button
      type="button"
      onClick={() => setViewingArchive(true)}
      className={`${mutedButton} min-h-[36px] mt-1`}
      style={{ color: colors.textMuted }}
    >
      Yesterday's conversation
    </button>
  );
  const archiveReader = reading && (
    <>
      {archive.entries.map((entry) => <Entry key={entry.id} entry={entry} colors={colors} />)}
      <div className="pt-3">
        <button
          type="button"
          onClick={() => { continueArchive(); setViewingArchive(false); }}
          disabled={busy}
          className={`${mutedButton} min-h-[36px]`}
          style={{ color: colors.textPrimary }}
        >
          Continue this conversation
        </button>
      </div>
    </>
  );
  const readingLabel = reading && <span className="font-mono text-xs" style={{ color: colors.textSecondary }}>Yesterday · {archiveDay}</span>;
  const backToToday = reading && (
    <button type="button" onClick={() => setViewingArchive(false)} className={mutedButton} style={{ color: colors.textSecondary }}>
      Back to today
    </button>
  );

  if (fill) {
    const topRow = reading ? (
      <div className="flex justify-between items-baseline gap-3 pb-1">
        {readingLabel}
        {backToToday}
      </div>
    ) : aside && <div className="flex justify-end pb-1">{aside}</div>;

    return (
      <section className="flex flex-col h-full min-h-0" aria-label="Assistant">
        {topRow}
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col"
          style={scrolled ? { maskImage: TOP_FADE, WebkitMaskImage: TOP_FADE } : undefined}
        >
          {/* An empty conversation sits at the bottom, by the text box, the
              way a chat starts */}
          <div className={empty && !reading ? 'mt-auto' : ''} aria-live="polite">
            {needsUpgrade ? upgrade : reading ? archiveReader : empty ? (
              <>
                {greeting && (
                  <p className="text-[22px] italic leading-snug" style={{ fontFamily: fonts.serif, color: colors.textPrimary }}>
                    {greeting}
                  </p>
                )}
                {chips}
                {archiveLink}
              </>
            ) : log}
          </div>
        </div>
        {!needsUpgrade && (
          // Holds the one-line composer's room; a longer message grows the
          // box upward over the conversation instead of pushing anything
          <div className="relative flex-none" style={{ height: COMPOSER_SLOT }}>
            <div className="absolute left-0 right-0 bottom-0" style={{ backgroundColor: colors.bg }}>{composer}</div>
          </div>
        )}
      </section>
    );
  }

  return (
    <Section spacing="mt-6" title="Ask" aside={reading ? backToToday : aside}>
      {reading ? readingLabel : briefLine}
      {needsUpgrade ? upgrade : (
        <>
          {(reading || entries.length > 0) && (
            <div
              ref={scrollRef}
              className="overflow-y-auto overscroll-contain pb-3"
              style={{ maxHeight: LOG_MAX_HEIGHT, borderBottom: `1px solid ${colors.border}` }}
              aria-live="polite"
            >
              {reading ? archiveReader : log}
            </div>
          )}
          {composer}
          {entries.length === 0 && !reading && (
            <>
              {suggestions(false)}
              {archiveLink}
            </>
          )}
        </>
      )}
    </Section>
  );
};

export default AgentChat;
